import { useEffect, useMemo, useState } from "react";
import { useDebounce, useWindowSize } from "@uidotdev/usehooks";
import {
  Brush,
  Label,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { UNIT_SYSTEM, WEIGHT_UNIT } from "@/shared/models";
import {
  DEFAULT_DEBOUNCE_TIME,
  PERIOD,
} from "@/shared/models/legacy-constants";
import { useSettingsStore } from "@/shared/stores";
import { convertPoundsToKg } from "@/shared/utils";
import { useWeightEntries } from "../../context";
import { CustomYAxisTick, WeightTooltipContent } from "./components";
import { formatXTick } from "./utils";

const WIDTH_LG = 1024;
const MAX_BRUSH_DISTANCE = 2;
const MIN_ENTRIES = 5;

interface BrushStartEndIndex {
  startIndex?: number;
  endIndex?: number;
}

export const WeightProgressChart = () => {
  const { data, view } = useWeightEntries();
  const { width } = useWindowSize();
  const debouncedWidth = useDebounce(width, DEFAULT_DEBOUNCE_TIME);
  const unitSystem = useSettingsStore((state) => state.unitSystem);

  const chartData = useMemo(
    () =>
      data.map((entry) => {
        const weight =
          unitSystem === UNIT_SYSTEM.METRIC
            ? convertPoundsToKg(entry.weight)
            : entry.weight;

        return { ...entry, y: weight, weight };
      }),
    [data, unitSystem],
  );

  const [tooltipActive, setTooltipActive] = useState(false);
  const [brushIndex, setBrushIndex] = useState<BrushStartEndIndex>();

  const dataLength = chartData.length;
  const showBrush = dataLength > MIN_ENTRIES;

  useEffect(() => {
    if (!showBrush || !debouncedWidth) {
      return;
    }

    setBrushIndex({
      startIndex: 0,
      endIndex: debouncedWidth < WIDTH_LG ? MIN_ENTRIES - 1 : dataLength - 1,
    });
  }, [debouncedWidth, showBrush, dataLength]);

  const onBrushChange = (index: BrushStartEndIndex) => {
    if (!brushIndex || !showBrush) {
      return;
    }

    const startIndex = index?.startIndex ?? 0;
    const endIndex = index?.endIndex ?? 0;
    const distance = Math.abs(endIndex - startIndex);

    if (distance > MAX_BRUSH_DISTANCE) {
      setBrushIndex(index);
    } else {
      setBrushIndex((prev) => ({
        startIndex:
          prev?.startIndex === startIndex
            ? startIndex
            : Math.max(endIndex - 2, 0),
        endIndex:
          prev?.endIndex === endIndex
            ? endIndex
            : Math.min(startIndex + 2, dataLength),
      }));
    }

    setTooltipActive(false);
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ScatterChart
        data={chartData}
        onMouseLeave={() => setTooltipActive(false)}
        height={300}
        margin={{
          top: 30,
          right: 30,
          bottom: 16,
          left: -30,
        }}
      >
        <XAxis
          dataKey="x"
          scale={dataLength > 1 ? "linear" : "auto"}
          height={showBrush ? 40 : undefined}
          label={
            <Label
              position="insideTopRight"
              className="translate-x-6 -translate-y-5 pb-2 text-sm text-neutral-950"
            >
              t
            </Label>
          }
          tick={{ fill: "#292929", fontSize: 12 }}
          padding={{ left: 20 }}
          tickFormatter={(tick: string) =>
            view === PERIOD.YEARLY ? formatXTick(tick) : tick
          }
        />

        <YAxis
          tickCount={6}
          dataKey="y"
          tickMargin={25}
          padding={{ top: 20 }}
          tick={<CustomYAxisTick />}
          label={
            <Label
              position="top"
              className="translate-x-8 -translate-y-2 pb-2 text-sm text-neutral-950"
            >
              {`wt (${WEIGHT_UNIT[unitSystem]})`}
            </Label>
          }
        />
        <ZAxis dataKey="weight" type="number" range={[500, 500]} />

        <Tooltip
          trigger="click"
          active={tooltipActive}
          offset={0}
          cursor={{
            strokeDasharray: "3 3",
          }}
          content={(tooltipData) => (
            <WeightTooltipContent
              tooltipData={tooltipData}
              onClose={() => setTooltipActive(false)}
            />
          )}
        />

        <Scatter
          name="weight"
          fill="#f6b853"
          cursor="pointer"
          line={{
            strokeDasharray: "5 4",
          }}
          shape="circle"
          onClick={() => setTooltipActive(true)}
        />

        {showBrush && (
          <Brush
            height={10}
            onChange={onBrushChange}
            tickFormatter={() => ""}
            startIndex={brushIndex?.startIndex}
            endIndex={brushIndex?.endIndex}
            stroke="#292929"
            fill="#292929"
            className="[&>.recharts-layer.recharts-brush-traveller]:hidden md:[&>.recharts-layer.recharts-brush-traveller]:block"
          />
        )}
      </ScatterChart>
    </ResponsiveContainer>
  );
};
